export interface Point {
  x: number;
  y: number;
}

export interface Rect extends Point {
  width: number;
  height: number;
}

export function constrain(value: number, min: number, max: number) {
  return Math.max(min, Math.min(value, max));
}

export function distanceSquare(x1: number, y1: number, x2: number, y2: number) {
  const dx = x1 - x2;
  const dy = y1 - y2;

  return dx * dx + dy * dy;
}

export function distance(x1: number, y1: number, x2: number, y2: number) {
  return Math.sqrt(distanceSquare(x1, y1, x2, y2));
}

export function isWithin(x1: number, y1: number, x2: number, y2: number, amount: number) {
  return distanceSquare(x1, y1, x2, y2) <= amount * amount;
}

export function radians(angle: number) {
  return angle * (Math.PI / 180);
}

export function boundingBox(rects: Array<Rect>) {
  const valid = rects.filter((r) => r != null);

  if (valid.length <= 0) {
    return null;
  }

  const minX = valid.reduce(function (initial, rect) {
    return Math.min(initial, rect.x);
  }, Infinity);

  const maxX = valid.reduce(function (initial, rect) {
    return Math.max(initial, rect.x + (rect.width || 0));
  }, minX);

  const minY = valid.reduce(function (initial, rect) {
    return Math.min(initial, rect.y);
  }, Infinity);

  const maxY = valid.reduce(function (initial, rect) {
    return Math.max(initial, rect.y + (rect.height || 0));
  }, minY);

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

export function center(rect: Rect) {
  return {
    x: rect.x + (rect.width || 0) * 0.5,
    y: rect.y + (rect.height || 0) * 0.5
  };
}

export function rotatePoint(point: Point, radians: number) {
  return {
    x: point.x * Math.cos(radians) - point.y * Math.sin(radians),
    y: point.x * Math.sin(radians) + point.y * Math.cos(radians)
  };
}

export function containsPoint(point: Point, box: Rect) {
  if (point.x != constrain(point.x, box.x, box.width)) {
    return false;
  }

  if (point.y != constrain(point.y, box.y, box.width)) {
    return false;
  }

  return true;
}

export function constrainPoint(point: Point, box: Rect) {
  const x = constrain(point.x, box.x, box.width);
  const y = constrain(point.y, box.y, box.height);

  if (x != point.x || y != point.y) {
    return { x, y };
  } else {
    return point;
  }
}

export function midpoint(pointA: Point, pointB: Point) {
  return {
    x: (pointA.x + pointB.x) / 2,
    y: (pointA.y + pointB.y) / 2
  };
}

/**
 * Safely divides two numbers and multiplies it by 100
 */
export function percent(a: number, b: number) {
  if (b == 0 || isFinite(b) == false) {
    return 0;
  }

  return (a / b) * 100;
}

export function slope(a: Point, b: Point) {
  if (a && b) {
    return Math.atan2(b.y - a.y, b.x - a.x);
  }

  return 0;
}

export function moveAway(point: Point, origin: Point, distance: number) {
  const angle = slope(origin, point);

  return {
    x: point.x + distance * Math.cos(angle),
    y: point.y + distance * Math.sin(angle)
  };
}
